import type { ReactElement } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { TrendingDown, TrendingUp, PlusCircle, Info } from 'lucide-react';
import type { RecommendationPanelDto, RecommendationKind } from '@bmai/licenses-api-client';
import { SurfaceCard } from '@/design/SurfaceCard';

interface ReassignmentPanelProps {
    recommendations: RecommendationPanelDto[];
    estReclaimedMonthly: number;
    spendCurrency: string;
    hasSpendData: boolean;
}

const KIND_STYLE: Record<RecommendationKind, { color: string; Icon: React.ElementType }> = {
    reduce: { color: 'var(--bm-color-seat-idle)', Icon: TrendingDown },
    elevate: { color: 'var(--mantine-primary-color-filled)', Icon: TrendingUp },
    expand: { color: 'var(--bm-color-success)', Icon: PlusCircle },
};

function formatCurrency(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
    } catch {
        return `${Math.round(amount).toLocaleString()} ${currency}`;
    }
}

export default function ReassignmentPanel({
    recommendations,
    estReclaimedMonthly,
    spendCurrency,
    hasSpendData,
}: ReassignmentPanelProps): ReactElement {
    return (
        <SurfaceCard>
            <Group justify="space-between" align="center" mb="lg">
                <Text size="sm" fw={700}>
                    Recommended reassignments
                </Text>
                <Text c="dimmed" fw={700} style={{ fontSize: '10px', letterSpacing: '0.05em' }}>
                    WITHIN-POOL · NO PROCUREMENT REQUIRED
                </Text>
            </Group>

            {recommendations.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No reassignment opportunities detected in the current window.
                </Text>
            ) : (
                <Stack gap="lg">
                    {recommendations.map((panel) => {
                        const style = KIND_STYLE[panel.kind];
                        const Icon = style.Icon;
                        return (
                            <Box key={panel.kind}>
                                <Group gap={8} mb="xs" align="center">
                                    <Icon size={15} color={style.color} />
                                    <Text size="sm" fw={700}>
                                        {panel.title}
                                    </Text>
                                </Group>
                                <Stack gap={6} pl={23}>
                                    {panel.items.map((item, index) => (
                                        <Text key={index} size="sm" c="dimmed">
                                            {item}
                                        </Text>
                                    ))}
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            <Group
                justify="space-between"
                align="center"
                mt="xl"
                pt="md"
                style={{ borderTop: '1px solid var(--bm-color-border)' }}
            >
                <Group gap={6} align="center">
                    <Info size={13} style={{ color: 'var(--bm-color-text-dimmed)' }} />
                    <Text c="dimmed" style={{ fontSize: '11px' }}>
                        Pool size changes still happen in each provider&apos;s admin console.
                    </Text>
                </Group>
                {hasSpendData && estReclaimedMonthly > 0 && (
                    <Text c="dimmed" style={{ fontSize: '11px' }}>
                        Est. value reclaimed:{' '}
                        <Text span fw={700} c="var(--bm-color-text)">
                            {formatCurrency(estReclaimedMonthly, spendCurrency)}/mo
                        </Text>
                    </Text>
                )}
            </Group>

            <Text c="dimmed" mt="sm" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                Department-level routing to team waitlists is coming soon.
            </Text>
        </SurfaceCard>
    );
}
